import { useMemo, useState } from 'react'

import { uniqBy } from 'lodash'

import { useActiveOrg } from '~/hooks/org'
import { useAddNewPasskey } from '~/hooks/useAddNewPasskey'
import { OrgSigner } from '~/types'

export const useSignerSelection = ({
  baseSigners,
  initialSigners,
  initialThreshold,
}: {
  baseSigners: OrgSigner[]
  initialSigners: OrgSigner[]
  initialThreshold: number
}) => {
  const { org } = useActiveOrg()
  const { addNewPasskey } = useAddNewPasskey()

  const [selectedSigners, setSelectedSigners] =
    useState<OrgSigner[]>(initialSigners)
  const [threshold, setThreshold] = useState(initialThreshold)

  const availableSigners = useMemo(
    () =>
      uniqBy(
        [...baseSigners, ...(org?.passkeys ?? []), ...selectedSigners],
        'id',
      ),
    [baseSigners, org?.passkeys, selectedSigners],
  )

  // No threshold auto-clamp on deselect — dialogs block saving with a reason.
  // Exception: at one signer 1 is the only valid threshold and the select
  // hides, so without the clamp an invalid threshold would be unfixable.
  const setSigner = (signer: OrgSigner, enabled: boolean) => {
    const nextSigners = enabled
      ? [...selectedSigners, signer]
      : selectedSigners.filter((s) => s.id !== signer.id)
    setSelectedSigners(nextSigners)
    if (nextSigners.length === 1) setThreshold(1)
  }

  const handleAddNewPasskey = async () => {
    const passkey = await addNewPasskey()
    if (passkey) setSelectedSigners((prev) => [...prev, passkey])
  }

  return {
    org,
    selectedSigners,
    threshold,
    setThreshold,
    availableSigners,
    setSigner,
    handleAddNewPasskey,
  }
}
